import moment from "moment";
import { useEffect, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { useHistory, useParams } from "react-router-dom";
import LoadingComponent from "../../components/loading-component";
import languageService from "../../services/core/language-service";
import StringHelpers from "../../services/core/string-helpers";
import Member from "../../services/member/member";
import Microsite from "../../services/micro-site/microsite";
import { MicrositeHelpers } from "../../services/micro-site/microsite-helpers";
import micrositeService from "../../services/micro-site/microsite-service";
import ReservationBlock from "../../services/micro-site/reservationBlock";
import "./micro-site-page.scss";
import MicrositeEventsCalendar from "./microsite-events-calendar";

type Props = {
  member: Member | null;
  setNoConnection: any;
};

interface IParamTypes {
  micrositeId: string;
}

export default function MicroSitePage(props: Props) {
  const { member, setNoConnection } = props;

  const { t } = useTranslation();
  const language: string = languageService.getLanguage();
  const history = useHistory();
  const { micrositeId } = useParams<IParamTypes>();

  const [loading, setLoading] = useState<boolean>(true);
  const [microsite, setMicrosite] = useState<Microsite>();
  const [reservationBlocks, setReservationBlocks] = useState<ReservationBlock[]>([]);

  useEffect(() => {
    micrositeService
      .getMicrosite(micrositeId)
      .then((microsite: Microsite | undefined) => {
        if (microsite) {
          setMicrosite(microsite);
          if (microsite.reservationBlocks[0]) {
            setReservationBlocks(
              microsite.reservationBlocks.sort((rb1, rb2) => new Date(rb1.startOn).getTime() - new Date(rb2.startOn).getTime())
            );
          }
        }
      })
      .catch(() => setNoConnection(true))
      .finally(() => {
        setLoading(false);
      });
  }, [member, setNoConnection, micrositeId]);

  if (loading) {
    return <LoadingComponent loading={loading} />;
  }

  if (!microsite) {
    return (
      <div className="container page micro-site-page">
        {!microsite && (
          <div className="text-center mt-4">
            <p>{t("micro-site-page.no-reservationBlock")}</p>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="container page micro-site-page ">
      <div className="row">
        <div className="col-12 offset-lg-2 col-lg-8 page-background-boxing shadow">
          {/* No reservation */}
          {reservationBlocks.length === 0 && (
            <div className="row">
              <div className="col-12 offset-lg-2 col-lg-8">
                <div className="card-body">
                  <h1>{microsite.name.get(language)}</h1>
                  <h5>{t("micro-site-page.no-reservationBlock")}</h5>
                </div>
              </div>
            </div>
          )}

          {/* Display the reservations */}
          {reservationBlocks.map((reservationBlock) => {
            const until = moment(reservationBlock.startOn).add(reservationBlock.duration, "seconds").toDate();
            const priceFormatted = StringHelpers.formatPrice(
              reservationBlock.price + reservationBlock.price * reservationBlock.saleTaxRate
            );

            return (
              <div className="card" key={reservationBlock.id}>
                <div className="card-header">
                  {reservationBlock?.logoUrl && <img src={reservationBlock?.logoUrl} alt="" className="img-logo" />}
                  <h2 className="mt-4"> {reservationBlock.name.get(language)}</h2>
                </div>

                <div className="card-body">
                  {microsite.logoUrl && <img src={microsite.logoUrl} alt="" className="img-logo float-end" />}
                  <p>
                    {reservationBlock.description && reservationBlock.description.length > 0 && (
                      <>
                        {reservationBlock.description.get(language)}
                        <br />{" "}
                      </>
                    )}
                    {reservationBlock.startOn && (
                      <Trans
                        i18nKey="micro-site-page.locker-available"
                        values={{
                          start: StringHelpers.formatDateTime(new Date(reservationBlock.startOn), StringHelpers.UserTimeZone),
                          end: StringHelpers.formatDateTime(until, StringHelpers.UserTimeZone),
                        }}
                      />
                    )}
                    {!reservationBlock.startOn && (
                      <Trans
                        i18nKey="micro-site-page.locker-available-now"
                        values={{ duration: StringHelpers.formatDuration(reservationBlock.duration) }}
                      />
                    )}
                  </p>

                  <p>{t("micro-site-page.cost", { cost: priceFormatted, currency: microsite.currency })}</p>

                  {!canShowCreateReservation(reservationBlock) && (
                    <p>
                      {t("micro-site-page.reservation-not-available", {
                        date: StringHelpers.formatDateTime(new Date(reservationBlock.availableStart.toString()), "America/Toronto"),
                      })}
                    </p>
                  )}

                  {canShowCreateReservation(reservationBlock) && (
                    <div className="mt-4">
                      <button className="btn btn-lg btn-primary" onClick={() => reserve(reservationBlock)}>
                        {t("micro-site-page.create-reservation")}
                      </button>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );

  function canShowCreateReservation(reservationBlock: ReservationBlock): boolean {
    return MicrositeHelpers.reservationBlockIsAvailable(reservationBlock);
  }

  async function reserve(reservationBlock: ReservationBlock) {
    const reserveUrl = `/sites/${micrositeId}/signin-reserve-locker/${reservationBlock?.id}`;
    history.push(reserveUrl);
  }
}
